// @flow
import { isValid } from 'date-fns';
import { dateUtils } from '../../../../lib/date-utils';
import FormatService from '../../../../lib/format';
import I18n from '../../../../lib/i18n';
import { eventTypeToSubtitleMap } from './eventTypeToSubtitle';

export const formatDatesAndAmountsParameters = (type: string, parameters: Object): Object => {
  let formattedParameters = { ...parameters };

  const amountLabels = ['amount', 'Amount', 'AMOUNT'];

  const dateParametersMap = { dateToCallWalletForfeiture: 'P' };
  const dateParametersKeys = Object.keys(dateParametersMap);

  Object.keys(formattedParameters).forEach(parameterKey => {
    const parameterValue = formattedParameters[parameterKey];

    // format amounts
    amountLabels.forEach(amountLabel => {
      if (parameterKey.includes(amountLabel)) {
        formattedParameters[parameterKey] = FormatService.formatCentsToCurrency(parameterValue, true);
      }
    });

    // format dates
    if (dateParametersKeys.includes(parameterKey)) {
      const parameterValueInNumber =
        typeof parameterValue === 'string' ? dateUtils.parse(parameterValue, 'T') : parameterValue;

      if (isValid(parameterValueInNumber)) {
        formattedParameters[parameterKey] = dateUtils.format(parameterValueInNumber, dateParametersMap[parameterKey]);
      }
    }
  });

  return formattedParameters;
};

export const getTitle = (type: string, title?: string, parameters?: Object = {}): string => {
  if (title) {
    return title;
  }

  let timelineType = type;

  const formattedDatesAndAmountsParameters = formatDatesAndAmountsParameters(type, parameters);

  if (type === 'TOPUP_OK') {
    const topUpType = parameters && getTopUpTitle(parameters);

    if (topUpType) {
      timelineType = topUpType;
    }
  }

  // Handling event line title for CHANGEPLAN
  if (type === 'CHANGEPLAN_SCHEDULED') {
    const orderItems = parameters.sandBox.productOrder.orderItems;
    if (orderItems) {
      const voucherObject = orderItems.filter(item => 'PVOUCHER' === item.product.name)[0];
      if (voucherObject) {
        const scheduledChangeplanTitle = I18n.t(`timeline.${timelineType}`, { ...formattedDatesAndAmountsParameters });
        const voucherLabelCode = voucherObject.product.characteristics.labelCode;
        const finalTitle =
          scheduledChangeplanTitle +
          '\n' +
          I18n.t('VoucherDetails.notificationSuffixLabel', {
            value: I18n.t(
              `productOffering.PVOUCHER.productSpecification.characteristics.labelCode.${voucherLabelCode}.label`
            ),
          });
        return finalTitle;
      }
    }
  }

  return I18n.has(`timeline.${timelineType}`)
    ? I18n.t(`timeline.${timelineType}`, { ...formattedDatesAndAmountsParameters })
    : timelineType;
};

export const getTopUpTitle = (parameters?: Object): string | null => {
  const topUpIddRegex = /TO_ZONE[A-Z]+_.*$/;
  const topUpRoamingRegex = /ROA[1-9]+_.*$/;
  const topUpPremiumRegex = /PREMIUM_USAGES_.*$/;

  let hasMatched = false;
  let title = '';
  parameters &&
    Object.keys(parameters).some(key => {
      if (topUpIddRegex.test(key)) {
        title = 'IDD_OK';
        hasMatched = true;
      } else if (topUpRoamingRegex.test(key)) {
        title = 'ROAMING_OK';
        hasMatched = true;
      } else if (topUpPremiumRegex.test(key)) {
        title = 'PREMIUM_OK';
        hasMatched = true;
      }
      return hasMatched;
    });

  return title || null;
};

export const getSubtitle = (
  subtitle: ?string,
  type?: string,
  parameters?: Object = {},
  phoneLine?: string
): ?string => {
  if (subtitle) {
    return subtitle;
  }

  if (type && eventTypeToSubtitleMap[type]) {
    return eventTypeToSubtitleMap[type]({ type, parameters }) ?? '';
  }

  const formattedDatesAndAmountsParameters = type && formatDatesAndAmountsParameters(type, parameters);

  const isNotificationWithAmount = type && I18n.has(`timeline.SUBTITLE_${type}`);

  if (type && isNotificationWithAmount) {
    return I18n.t(`timeline.SUBTITLE_${type}`, formattedDatesAndAmountsParameters);
  }

  if (phoneLine === 'NOT_USED_FOR_CREDIT_WALLET') {
    return null;
  }

  return phoneLine;
};
